import React, {useEffect, useRef, useState} from 'react';
import {
  Fab,
  Stack,
  styled,
  Typography,
  useMediaQuery,
  Zoom,
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {AutoFixHigh, Check} from '@mui/icons-material';
import {Theme} from '@mui/material/styles';
import {useSearchParam} from 'react-use';
import {
  TruvuDialogTransition,
  useTruvuDialogStyles,
} from './dialog/TruvuDialog2';

export function AIImageEditButton() {
  const disabledControls = useSearchParam('disabledControls');
  const liveViewing = useSearchParam('liveViewing');
  const splitDisableControls = disabledControls?.toLowerCase()?.split(',');

  const classes = useTruvuDialogStyles();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md')
  );

  const [dialogStatus, setDialogStatus] = useState<
    'idle' | 'active' | 'processing' | 'success' | 'error'
  >('idle');
  const [resultImage, setResultImage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const timeoutRef = useRef<NodeJS.Timeout>();

  // Find canvas in DOM - looks for the tour canvas
  const getCanvasFromDOM = (): HTMLCanvasElement | null => {
    const canvas = document.querySelector('canvas') as HTMLCanvasElement;
    if (!canvas) {
      console.error('No canvas found in DOM');
      return null;
    }
    return canvas;
  };

  // Convert canvas to blob/file
  const getCurrentCanvasImage = async (): Promise<File | null> => {
    const canvas = getCanvasFromDOM();
    if (!canvas) return null;

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const file = new File([blob], 'tour-frame.jpg', {
              type: 'image/jpeg',
            });
            resolve(file);
          } else {
            console.error('Failed to convert canvas to blob');
            resolve(null);
          }
        },
        'image/jpeg',
        0.85
      );
    });
  };

  // Call AI worker
  const callAIWorker = async (prompt: string, actionName: string) => {
    console.log(`Starting ${actionName}...`);

    const imageFile = await getCurrentCanvasImage();
    if (!imageFile) {
      setErrorMessage('Could not capture canvas image');
      setDialogStatus('error');
      return;
    }

    setDialogStatus('processing');
    setErrorMessage('');
    setResultImage(null);

    try {
      // Get image dimensions
      const img = new Image();
      const imageUrl = URL.createObjectURL(imageFile);

      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = imageUrl;
      });

      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;
      URL.revokeObjectURL(imageUrl);

      // Prepare form data
      const formData = new FormData();
      formData.append('image', imageFile);
      formData.append('prompt', prompt);
      formData.append('width', naturalWidth.toString());
      formData.append('height', naturalHeight.toString());

      console.log(`Sending to AI worker: ${actionName}`);

      // Call AI worker
      const response = await fetch(
        'https://llama-vision-test.waldo-4ef.workers.dev/inpainting',
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP error! status: ${response.status}, message: ${errorText}`
        );
      }

      // Get result
      const resultBlob = await response.blob();
      const resultImageUrl = URL.createObjectURL(resultBlob);

      setResultImage(resultImageUrl);
      setDialogStatus('success');

      console.log(`${actionName} completed!`, resultImageUrl);

      // Auto-close after 5 seconds on success
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setDialogStatus('idle');
        setResultImage(null);
      }, 5000);
    } catch (error) {
      console.error(`Error during ${actionName}:`, error);
      setErrorMessage(
        `Error during ${actionName}: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
      setDialogStatus('error');
    }
  };

  const handleRemoveFurniture = () => {
    callAIWorker(
      'Remove all furniture and objects from the room, create clean empty walls and flooring, whitebox interior, empty real estate space',
      'Remove Furniture'
    );
  };

  const handleModernStyle = () => {
    callAIWorker(
      'Replace all furniture with modern, minimalist furniture and decor. Use contemporary design with clean lines, neutral colors (whites, grays, beiges), and stylish modern pieces. Include modern lighting, minimal artwork, and sophisticated staging for luxury real estate.',
      'Modern Style'
    );
  };

  const handleCloseDialog = () => {
    setDialogStatus('idle');
    setResultImage(null);
    setErrorMessage('');
    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current);
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
      }
      if (resultImage) {
        URL.revokeObjectURL(resultImage);
      }
    };
  }, [resultImage]);

  return (
    <>
      <Dialog
        open={
          dialogStatus === 'active' ||
          dialogStatus === 'processing' ||
          dialogStatus === 'success' ||
          dialogStatus === 'error'
        }
        onClose={handleCloseDialog}
        aria-labelledby="ai-edit-dialog"
        aria-describedby="ai-edit-dialog-description"
        TransitionComponent={TruvuDialogTransition}
        maxWidth="md"
        fullWidth
        classes={{root: classes.container, paper: classes.paperMD}}
      >
        <DialogTitle>
          <Typography variant={'h1'} fontWeight={700} mb={2}>
            AI Image Editing
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {dialogStatus === 'active' && (
              <>
                <Typography variant="body1" mb={2}>
                  Choose an AI transformation for the current frame:
                </Typography>
                <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                  <Button
                    variant="contained"
                    onClick={handleRemoveFurniture}
                    fullWidth
                    sx={{flex: 1}}
                  >
                    <span role="img" aria-label="cleaning brush">
                      🧹
                    </span>{' '}
                    Remove Furniture
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleModernStyle}
                    fullWidth
                    sx={{flex: 1}}
                  >
                    <span role="img" aria-label="house">
                      🏠
                    </span>{' '}
                    Modern Style
                  </Button>
                </Stack>
              </>
            )}

            {dialogStatus === 'processing' && (
              <Stack alignItems="center" spacing={2}>
                <CircularProgress size={60} />
                <Typography variant="body1">
                  Processing image... please wait
                </Typography>
              </Stack>
            )}

            {dialogStatus === 'success' && resultImage && (
              <Stack spacing={2}>
                <Alert severity="success">
                  AI transformation completed successfully!
                </Alert>
                <img
                  src={resultImage}
                  alt="AI Transformed Result"
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '8px',
                    border: '1px solid #ccc',
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={() => window.open(resultImage, '_blank')}
                  fullWidth
                >
                  Open in New Tab
                </Button>
              </Stack>
            )}

            {dialogStatus === 'error' && (
              <Alert
                severity="error"
                sx={{
                  '& .MuiAlert-message': {
                    color: '#d32f2f',
                    fontWeight: 500,
                    wordBreak: 'break-word',
                  },
                  backgroundColor: '#ffebee',
                  border: '1px solid #ffcdd2',
                }}
              >
                {errorMessage}
              </Alert>
            )}

            <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} mt={2}>
              <Button
                variant="outlined"
                onClick={handleCloseDialog}
                fullWidth
                sx={{flex: 1}}
              >
                Close
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>

      <Zoom in={!splitDisableControls?.includes('ai-edit') && !liveViewing}>
        <AIEditFab
          variant={isMobile ? 'circular' : 'extended'}
          color={dialogStatus === 'success' ? 'success' : 'primary'}
          size={isMobile ? 'small' : 'medium'}
          aria-label="ai-edit-button"
          onClick={() => setDialogStatus('active')}
          disabled={dialogStatus === 'processing'}
        >
          {dialogStatus === 'success' ? <Check /> : <AutoFixHigh />}
          {!isMobile && (
            <Typography ml={0.5}>
              {dialogStatus === 'success' ? 'AI Complete' : 'AI Edit'}
            </Typography>
          )}
        </AIEditFab>
      </Zoom>
    </>
  );
}

const AIEditFab = styled(Fab)(() => ({
  position: 'fixed',
  right: 16,
  bottom: 80, // Position above the feedback button
  zIndex: 1100,
}));
